import { useEffect, useRef, useState, Dispatch, SetStateAction } from "react";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import { ZoomModalImage } from "./ZoomModalImage";
import { ZoomModalVideo } from "./ZoomModalVideo";
import { Thumbnails } from "./Thumbnails";
import { GalleryObject } from "@/components/Galleries/utils/utils.ts";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import styles from "./Carousel.module.scss";

type CarouselProps = {
  mediaItems: GalleryObject[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  closeZoomModal: () => void;
  videoAutoplayIndex: number | null;
};

type CarouselItemProps = {
  mediaItem: GalleryObject;
  totalMediaItems: number;
  index: number;
  intersectionObserver: IntersectionObserver | null;
  scrollToIndex: (index: number) => void;
  isActiveVideo: boolean;
  onVideoPlayStateChange: (index: number, isPlaying: boolean) => void;
  videoAutoplayIndex: number | null;
};

const CarouselItem = ({
  mediaItem,
  totalMediaItems,
  index,
  intersectionObserver,
  scrollToIndex,
  isActiveVideo,
  onVideoPlayStateChange,
  videoAutoplayIndex,
}: CarouselItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current && intersectionObserver) {
      intersectionObserver.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current && intersectionObserver) {
        intersectionObserver.unobserve(itemRef.current);
      }
    };
  }, [intersectionObserver]);

  if (!mediaItem) {
    return null;
  }

  return (
    <div
      key={mediaItem.imageUrl}
      data-gallery-item-index={index}
      className={styles.galleryItem}
      ref={itemRef}
    >
      {mediaItem.type === "video" ? (
        <ZoomModalVideo
          video={mediaItem}
          index={index}
          total={totalMediaItems}
          scrollToIndex={scrollToIndex}
          isActive={isActiveVideo}
          onPlayStateChange={onVideoPlayStateChange}
          videoAutoplayIndex={videoAutoplayIndex}
        />
      ) : (
        <ZoomModalImage
          mediaItem={mediaItem}
          index={index}
          total={totalMediaItems}
          onClick={() => {
            dispatchGalleryMonetateQEvent("ClickOnModalImage", "imageModal");
          }}
        />
      )}
    </div>
  );
};

export const Carousel = ({
  mediaItems,
  selectedIndex,
  setSelectedIndex,
  closeZoomModal,
  videoAutoplayIndex,
}: CarouselProps) => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenAnimationEnded, setfullscreenAnimationEnded] =
    useState(false);
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const [autoScroll, setAutoScroll] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const { flags } = useStandardProps();
  const hasDeferredMonetateQEvents = !!flags.hasDeferredMonetateQEvents.value;
  const modalMode =
    mediaItems[0]?.type === "video" ? "videoModal" : "imageModal";

  const isLandscapeLayout = () =>
    window.matchMedia("(orientation: landscape)").matches;

  const handleVideoPlayStateChange = (index: number, playing: boolean) => {
    setActiveVideoIndex((current) => {
      if (playing) return index;
      return current === index ? null : current;
    });
  };

  const scrollImagesToIndex = async (
    index: number,
    behaviour: ScrollBehavior = "smooth",
  ) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }
    const target = mainContainerRef.current.children[index];
    if (!(target instanceof HTMLElement)) return;

    let thumbnailHeight = 0;
    if (thumbsContainerRef.current && !isLandscapeLayout()) {
      thumbnailHeight =
        thumbsContainerRef.current.getBoundingClientRect().height;
    }
    const difference =
      (window.innerHeight - thumbnailHeight - target.clientHeight) / 2;
    const offsetTop =
      difference < 0
        ? target.offsetTop + Math.abs(difference)
        : target.offsetTop - Math.abs(difference);

    setAutoScroll(true);
    const result = await mainContainerRef.current.scrollTo({
      left: target.offsetLeft,
      top: offsetTop,
      behavior: behaviour,
    });
    setAutoScroll(false);
  };

  const scrollThumnailsToIndex = async (index: number) => {
    if (
      index < 0 ||
      index >= mediaItems.length ||
      !thumbsContainerRef.current
    ) {
      return;
    }
    const target = thumbsContainerRef.current.children[index];
    if (!(target instanceof HTMLElement)) return;

    const offsetTop = isLandscapeLayout()
      ? target.offsetTop -
        thumbsContainerRef.current.getBoundingClientRect().height / 2 +
        target.getBoundingClientRect().height / 2
      : target.offsetTop;
    const offsetLeft = !isLandscapeLayout()
      ? target.offsetLeft -
        thumbsContainerRef.current.getBoundingClientRect().width / 2 +
        target.getBoundingClientRect().width / 2
      : target.offsetLeft;
    thumbsContainerRef.current.scrollTo(offsetLeft, offsetTop);
  };

  const handleFullscreenAnimationEnd = () => {
    setfullscreenAnimationEnded(true);
    scrollImagesToIndex(selectedIndex, "instant");
    scrollThumnailsToIndex(selectedIndex);
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const isRelevant = (intersectionEntry: IntersectionObserverEntry) => {
      if (
        (isLandscapeLayout() &&
          intersectionEntry.intersectionRatio >= 0.3 &&
          intersectionEntry.intersectionRatio < 0.6) ||
        (!isLandscapeLayout() &&
          !(
            intersectionEntry.intersectionRatio >= 0.3 &&
            intersectionEntry.intersectionRatio < 0.6
          ))
      ) {
        return true;
      }
      return false;
    };

    const intersectingEntries = entries
      .filter(({ isIntersecting }) => isIntersecting)
      .filter(isRelevant);

    if (
      mainContainerRef.current &&
      intersectingEntries &&
      intersectingEntries.length > 0
    ) {
      const maxIntersecting = intersectingEntries.reduce((prev, current) => {
        return prev && prev.intersectionRatio > current.intersectionRatio
          ? prev
          : current;
      });

      const intersectionIndex = [...mainContainerRef.current.children].indexOf(
        maxIntersecting.target,
      );

      dispatchGalleryMonetateQEvent(
        "ModalImageScroll",
        modalMode,
        hasDeferredMonetateQEvents,
      );

      if (!autoScroll) {
        setSelectedIndex(intersectionIndex);
      }
    }
  };

  const handleScrollIntoView = () => {
    if (mainContainerRef.current) {
      mainContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    let observer: IntersectionObserver;
    if (fullscreenAnimationEnded && !intersectionObserver) {
      observer = new IntersectionObserver(handleIntersection, {
        root: mainContainerRef.current,
        threshold: [0.3, 0.6, 1],
      });

      setIntersectionObserver(observer);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [handleIntersection, intersectionObserver, fullscreenAnimationEnded]);

  useEffect(() => {
    const modalElement = mainContainerRef.current?.closest(
      '[data-testid="modal"]',
    );

    modalElement?.addEventListener(
      "animationstart",
      handleFullscreenAnimationEnd,
      {
        once: true,
      },
    );

    document.addEventListener(
      "carousel-gallery-scroll-into-view",
      handleScrollIntoView,
    );

    const root = document?.documentElement;
    if (root) {
      root.classList.forEach((value) => {
        if (value.includes("scrollbar-shift")) {
          root.classList.remove(value);
        }
      });
    }

    return () => {
      modalElement?.removeEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
      );

      document.removeEventListener(
        "carousel-gallery-scroll-into-view",
        handleScrollIntoView,
      );
    };
  }, []);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)");

    const handleResize = () => {
      setIsDesktop(isDesktop.matches);
    };
    handleResize();

    isDesktop.addEventListener("change", handleResize);

    return () => {
      window.removeEventListener("change", handleResize);
    };
  }, []);

  return (
    <div className={styles.wrapper} data-testid={"gallery:zoom:modal:carousel"}>
      <div className={styles.main}>
        <div className={styles.gallery} ref={mainContainerRef}>
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              totalMediaItems={mediaItems.length}
              intersectionObserver={intersectionObserver}
              index={ind}
              scrollToIndex={scrollImagesToIndex}
              isActiveVideo={activeVideoIndex === ind}
              onVideoPlayStateChange={handleVideoPlayStateChange}
              key={`${mediaItem.imageUrl}-${ind}`}
              videoAutoplayIndex={videoAutoplayIndex}
            />
          ))}
        </div>
        <Thumbnails
          media={mediaItems}
          selectedIndex={selectedIndex}
          scrollImagesToIndex={scrollImagesToIndex}
          scrollThumbnailsToIndex={scrollThumnailsToIndex}
          autoScroll={autoScroll}
          ref={thumbsContainerRef}
        />
      </div>
      <IconButton
        icon={CloseOutlined}
        aria-label={"Close button"}
        data-testid="gallery:zoom:modal:close:button"
        className={styles.closeButton}
        onClick={closeZoomModal}
        background="filled"
        size={isDesktop ? "medium" : "small"}
      />
      <div
        className={styles.visuallyHidden}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Viewing image ${selectedIndex + 1} of ${mediaItems.length}`}
      </div>
    </div>
  );
};
